import type { ReactNode } from 'react';

/**
 * ONE ROW OF A SHORT LIST OF CHOICES (UI-GLASS1, the 132nd, 2026-10-11).
 *
 * His words: "iOS just came out with new buttons, like a new style. Can we match it?" - and, narrowing it with a picture
 * of iOS 26's menu over a photo: only the app's own pop-ups that offer a few choices. This is that menu's row: a pill
 * as wide as the panel, tall enough for a thumb, on the frosted panel `Dialog look="choices"` draws.
 *
 * ⛔ FOR A POP-UP THAT OFFERS A FEW CHOICES, NOTHING ELSE. A form's Save and Cancel stay `Button` - his design rule for
 *   the rest of the app (square-ish corners, no shadow) is untouched, and he said so: "I don't want it to look all weird."
 */
interface ChoiceRowProps {
  label: string;
  /** One line under the label saying what the choice does. */
  hint?: string;
  icon?: ReactNode;
  /** `main`: the choice he most likely came for. `quiet`: a way out (Cancel), centred and plain. */
  tone?: 'plain' | 'main' | 'quiet';
  disabled?: boolean;
  onClick: () => void;
}

const toneStyles = {
  plain: 'bg-white/70 text-gray-900 hover:bg-white',
  main: 'bg-primary-500 text-white hover:bg-primary-600',
  quiet: 'justify-center text-center bg-transparent text-gray-600 hover:bg-white/50',
};

export const ChoiceRow = ({ label, hint, icon, tone = 'plain', disabled, onClick }: ChoiceRowProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex w-full min-h-[52px] items-center gap-3 rounded-[26px] px-5 py-3 text-left transition active:scale-[0.98] disabled:opacity-50 ${toneStyles[tone]}`}
  >
    {icon && <span className="shrink-0">{icon}</span>}
    <span className="min-w-0">
      <span className="block text-[15px] font-medium leading-tight">{label}</span>
      {hint && (
        <span className={`mt-0.5 block text-[12.5px] leading-snug ${tone === 'main' ? 'text-white/80' : 'text-gray-500'}`}>
          {hint}
        </span>
      )}
    </span>
  </button>
);
